import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import {
  FileText,
  Edit,
  ArrowUpDown,
  Search,
  Filter,
  Plus,
  CheckCircle,
  XCircle as XCircleIcon,
  X,
  AlertTriangle,
  User,
  Eye,
} from "lucide-react";
import { DocumentMaster } from "../../../services/documentMasterService";
import { userService } from "../../../services/userService";
import Select from "../../../shared/components/form/Select";
import api from "../../../services/axiosInstance";
import { BRAND_COLORS } from "../../../shared/constants/brandColors";

// DocumentFilter Component
type OptionType = { label: string; value: string };

interface DocumentFilterProps {
  status: string | null;
  setStatus: (val: string | null) => void;
}

const DocumentFilter = ({
  status,
  setStatus,
}: DocumentFilterProps) => {
  const { control } = useForm({});

  const statusOptions: OptionType[] = [
    { value: "", label: "All Status" },
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
  ];

  const handleResetFilters = () => {
    setStatus(null);
  };

  const hasActiveFilters = status;

  return (
    <div className="flex flex-wrap items-center gap-3 p-4 bg-white rounded-lg border border-gray-200">
      {/* Status Filter */}
      <div className="flex-1 min-w-[180px]">
        <Select
          options={statusOptions}
          value={statusOptions.find((opt) => opt.value === status)}
          placeholder="Filter by Status"
          onChange={(selectedOption) => {
            setStatus(selectedOption?.value === "" ? null : selectedOption?.value || null);
          }}
        />
      </div>

      {/* Reset Filters Button */}
      {hasActiveFilters && (
        <button
          type="button"
          onClick={handleResetFilters}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 hover:text-gray-700 hover:border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1"
          style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
        >
          <X size={14} />
          Clear
        </button>
      )}
    </div>
  );
};

// Enhanced ToggleStatusCell Component
interface ToggleStatusCellProps {
  document: DocumentMaster;
  onRefresh: () => void;
}

const ToggleStatusCell: React.FC<ToggleStatusCellProps> = ({ document, onRefresh }) => {
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [clickPosition, setClickPosition] = useState({ x: 0, y: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleToggle = async () => {
    setIsLoading(true);
    try {
      const documentId = document._id || document.id;
      if (!documentId) {
        toast.error("Document ID is missing");
        setIsLoading(false);
        return;
      }
      const endpoint = document.status === "Active" ? "inactivate" : "activate";
      const response = await api.patch(
        `/document-masters/${documentId}/${endpoint}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      const updatedStatus = response?.data?.data?.status || (document.status === "Active" ? "Inactive" : "Active");

      toast.success(`Document ${updatedStatus === "Active" ? "activated" : "deactivated"} successfully`);
      setConfirmOpen(false);
      onRefresh();
    } catch (err: any) {
      console.error("Toggle error:", err);
      toast.error("Failed to toggle document status");
    } finally {
      setIsLoading(false);
    }
  };

  const isActive = document.status === "Active";

  const handleOpenModal = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setClickPosition({
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2
    });
    setConfirmOpen(true);
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleOpenModal}
        title={`Click to ${isActive ? "deactivate" : "activate"}`}
        className={`relative inline-flex items-center justify-center w-10 h-5 rounded-full border transition-all duration-300 select-none ${
          isActive
            ? "bg-green-50 border-green-200 hover:bg-green-100"
            : "bg-gray-50 border-gray-200 hover:bg-gray-100"
        }`}
      >
        <div
          className={`w-3 h-3 rounded-full transform transition-all duration-300 ${
            isActive
              ? "translate-x-2 bg-green-500"
              : "-translate-x-2 bg-gray-400"
          }`}
        />
      </button>

      <AnimatePresence>
        {isConfirmOpen && (
          <div className="fixed inset-0 z-90 overflow-y-auto bg-gray-500/5">
            <div className="flex min-h-full items-center justify-end p-4">
              <motion.div
                initial={{
                  opacity: 0,
                  scale: 0.95,
                  x: clickPosition.x,
                  y: clickPosition.y,
                  z: -100
                }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  x: -20,
                  y: 0,
                  z: 0
                }}
                exit={{
                  opacity: 0,
                  scale: 0.95,
                  x: clickPosition.x,
                  y: clickPosition.y,
                  z: -100
                }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="relative w-full max-w-md bg-white rounded-lg border border-gray-200 shadow-[0_8px_30px_rgb(0,0,0,0.12)] mr-4"
              >
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${
                      isActive ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"
                    }`}>
                      {isActive ? (
                        <XCircleIcon size={20} className="text-red-500" />
                      ) : (
                        <CheckCircle size={20} className="text-green-500" />
                      )}
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {isActive ? "Deactivate Document" : "Activate Document"}
                      </h2>
                      <p className="text-sm text-gray-600">
                        {document.documentName}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setConfirmOpen(false)}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700 border border-gray-200 transition-all duration-200"
                  >
                    <X size={16} />
                  </button>
                </div>

                {/* Content */}
                <div className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-amber-50 rounded-lg flex items-center justify-center border border-amber-200">
                      <AlertTriangle size={14} className="text-amber-500" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                        Are you sure you want to{" "}
                        <span className="font-medium text-gray-900">
                          {isActive ? "deactivate" : "activate"}
                        </span>{" "}
                        <span className="font-medium" style={{ color: BRAND_COLORS.primary }}>{document.documentName}</span>?
                      </p>
                      <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                        <p className="text-xs text-gray-600 leading-relaxed">
                          {isActive
                            ? "This document will be marked as inactive and won't appear in active document lists."
                            : "This document will be reactivated and will appear in document lists again."
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-100 bg-gray-50 rounded-b-lg">
                  <button
                    onClick={() => setConfirmOpen(false)}
                    disabled={isLoading}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleToggle}
                    disabled={isLoading}
                    className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md transition-all duration-200 disabled:opacity-50 ${
                      isActive
                        ? "bg-red-500 hover:bg-red-600 focus:ring-red-500"
                        : "bg-green-500 hover:bg-green-600 focus:ring-green-500"
                    } focus:outline-none focus:ring-2 focus:ring-offset-2`}
                  >
                    {isLoading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <span>
                        {isActive ? "Deactivate" : "Activate"}
                      </span>
                    )}
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

interface Props {
  documents: DocumentMaster[];
  search: string;
  setSearch: (val: string) => void;
  status: string | null;
  setStatus: (val: string | null) => void;
  page: number;
  setPage: (val: number) => void;
  totalDocuments: number;
  onEdit?: (document: DocumentMaster) => void;
  onView?: (document: DocumentMaster) => void;
  onAdd?: () => void;
  onToggleStatus?: (document: DocumentMaster) => void;
  onRefresh: () => void;
  sortField: string;
  sortOrder: "asc" | "desc";
  setSortField: (field: string) => void;
  setSortOrder: (order: "asc" | "desc") => void;
  loading?: boolean;
}

const ITEMS_PER_PAGE = 6;

export default function DocumentTable({
  documents,
  search,
  setSearch,
  status,
  setStatus,
  page,
  setPage,
  totalDocuments,
  onEdit,
  onView,
  onAdd,
  onRefresh,
  sortField,
  sortOrder,
  setSortField,
  setSortOrder,
  loading = false,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);

  const [sortMenuOpen, setSortMenuOpen] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [userNames, setUserNames] = useState<Record<string, string>>({});

  // Fetch user names for createdBy
  useEffect(() => {
    const fetchUserNames = async () => {
      const userIds = new Set<string>();
      documents.forEach(document => {
        if (document.createdBy) {
          const userId = typeof document.createdBy === 'string' ? document.createdBy : document.createdBy.id;
          if (userId) userIds.add(userId);
        }
      });

      if (userIds.size === 0) return;

      try {
        const userPromises = Array.from(userIds).map(async (userId) => {
          try {
            const user = await userService.getUserById(userId);
            return { id: userId, name: user.name };
          } catch (error) {
            console.error(`Failed to fetch user ${userId}:`, error);
            return { id: userId, name: 'Unknown User' };
          }
        });

        const userResults = await Promise.all(userPromises);
        const nameMap: Record<string, string> = {};
        userResults.forEach(({ id, name }) => {
          nameMap[id] = name;
        });
        setUserNames(nameMap);
      } catch (error) {
        console.error('Error fetching user names:', error);
      }
    };

    if (documents.length > 0) {
      fetchUserNames();
    }
  }, [documents]);

  // Keyboard shortcut for search
  useEffect(() => {
    const handleShortcut = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  const totalPages = Math.ceil(totalDocuments / ITEMS_PER_PAGE);

  const handleSortOption = (field: string, order: "asc" | "desc" | "clear") => {
    if (order === "clear") {
      setSortField("createdAt");
      setSortOrder("desc");
    } else {
      setSortField(field);
      setSortOrder(order);
    }
    setPage(1); // Reset to first page when sort changes
    setSortMenuOpen(null);
  };

  const getUserName = (createdBy: string | { id: string; name: string; email: string } | undefined) => {
    if (!createdBy) return 'N/A';
    if (typeof createdBy === 'string') {
      return userNames[createdBy] || 'Loading...';
    }
    return createdBy.name || 'Unknown User';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-lg border border-gray-200 overflow-hidden"
    >
      {/* Controls Section */}
      <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-white to-gray-25">
        {/* Main Controls Row */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Left Side: Search and Filter */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            {/* Search Bar */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={16} className="text-gray-400" />
              </div>
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search documents... (Ctrl+K)"
                className="block w-full sm:w-80 pl-9 pr-4 py-2.5 text-sm font-medium border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all duration-200 bg-white"
                style={{
                  '--tw-ring-color': BRAND_COLORS.primary,
                  'focusBorderColor': BRAND_COLORS.primary
                } as React.CSSProperties}
              />
            </div>

            {/* Filter Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 border ${
                showFilters
                  ? `text-white border-transparent`
                  : "text-gray-600 bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400"
              }`}
              style={showFilters ? {
                background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`
              } : {}}
            >
              <Filter size={16} />
              Filters
            </button>
          </div>

          {/* Right Side: Sort and Add */}
          <div className="flex items-center gap-3">
            {/* Sort Button */}
            <div className="relative">
              <button
                onClick={() => setSortMenuOpen(sortMenuOpen ? null : "general")}
                className="inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 transition-all duration-200"
                style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
              >
                <ArrowUpDown size={16} />
                Sort
              </button>

              {/* Sort Dropdown */}
              <AnimatePresence>
                {sortMenuOpen === "general" && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full right-0 mt-2 z-20 w-48 bg-white border border-gray-200 rounded-lg overflow-hidden"
                  >
                    <div className="p-3">
                      <div className="text-xs font-medium text-gray-500 px-2 py-1 mb-2">Sort by Name</div>
                      <button
                        onClick={() => handleSortOption("documentName", "asc")}
                        className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${
                          sortField === "documentName" && sortOrder === "asc"
                            ? `font-medium bg-opacity-10`
                            : "text-gray-700"
                        }`}
                        style={sortField === "documentName" && sortOrder === "asc" ? {
                          color: BRAND_COLORS.primary,
                          backgroundColor: BRAND_COLORS.primaryLight
                        } : {}}
                      >
                        A → Z
                      </button>
                      <button
                        onClick={() => handleSortOption("documentName", "desc")}
                        className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${
                          sortField === "documentName" && sortOrder === "desc"
                            ? `font-medium bg-opacity-10`
                            : "text-gray-700"
                        }`}
                        style={sortField === "documentName" && sortOrder === "desc" ? {
                          color: BRAND_COLORS.primary,
                          backgroundColor: BRAND_COLORS.primaryLight
                        } : {}}
                      >
                        Z → A
                      </button>

                      <div className="border-t border-gray-200 my-2"></div>
                      <div className="text-xs font-medium text-gray-500 px-2 py-1 mb-2">Sort by Date</div>
                      <button
                        onClick={() => handleSortOption("createdAt", "desc")}
                        className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${
                          sortField === "createdAt" && sortOrder === "desc"
                            ? `font-medium bg-opacity-10`
                            : "text-gray-700"
                        }`}
                        style={sortField === "createdAt" && sortOrder === "desc" ? {
                          color: BRAND_COLORS.primary,
                          backgroundColor: BRAND_COLORS.primaryLight
                        } : {}}
                      >
                        Newest First
                      </button>
                      <button
                        onClick={() => handleSortOption("createdAt", "asc")}
                        className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${
                          sortField === "createdAt" && sortOrder === "asc"
                            ? `font-medium bg-opacity-10`
                            : "text-gray-700"
                        }`}
                        style={sortField === "createdAt" && sortOrder === "asc" ? {
                          color: BRAND_COLORS.primary,
                          backgroundColor: BRAND_COLORS.primaryLight
                        } : {}}
                      >
                        Oldest First
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Add Document Button */}
            <button
              onClick={onAdd}
              className="inline-flex items-center gap-3 px-5 py-2.5 text-sm font-medium text-white border border-transparent rounded-xl shadow-lg shadow-emerald-100 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-emerald-200"
              style={{
                background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
                '--tw-ring-color': BRAND_COLORS.primary
              } as React.CSSProperties}
            >
              <div className="relative">
                <Plus size={16} className="absolute animate-ping opacity-40" />
                <Plus size={16} className="relative" />
              </div>
              <span>Add Document</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-4"
            >
              <DocumentFilter
                status={status}
                setStatus={setStatus}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: BRAND_COLORS.primary }}></div>
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <tr>
                {[
                  { label: "Sr No", field: "srNo" },
                  { label: "Document Name", field: "documentName", icon: <FileText size={14} /> },
                  { label: "Created By", field: "createdBy", icon: <User size={14} /> },
                ].map(({ label, field, icon }) => (
                  <th
                    key={field}
                    className="p-4 text-left text-sm font-medium text-gray-700 tracking-wide"
                  >
                    <div className="flex items-center gap-2">
                      {icon}
                      <span>{label}</span>
                    </div>
                  </th>
                ))}
                <th className="p-4 text-center text-sm font-medium text-gray-700 tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              <AnimatePresence>
                {documents.map((document, index) => {
                  const srNo = (page - 1) * ITEMS_PER_PAGE + index + 1;
                  return (
                    <motion.tr
                      key={document._id || document.id || index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.2, delay: index * 0.05 }}
                      className={`transition-all duration-200 ${
                        document.status === "Inactive"
                          ? "bg-gray-50/50 opacity-60 hover:bg-gray-100/60"
                          : "hover:bg-gradient-to-r hover:from-gray-25 hover:to-gray-50"
                      }`}
                    >
                      {/* Sr No */}
                      <td className="p-4">
                        <span className={`text-sm font-medium ${
                          document.status === "Inactive" ? "text-gray-500" : "text-gray-900"
                        }`}>
                          {srNo}
                        </span>
                      </td>

                      {/* Document Name */}
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center border border-gray-300">
                              <FileText size={16} className="text-gray-600" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className={`text-sm font-medium truncate ${
                              document.status === "Inactive" ? "text-gray-500 line-through" : "text-gray-900"
                            }`}>
                              {document.documentName}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Created By */}
                      <td className="p-4">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2 mb-1">
                            <User size={14} className={document.status === "Inactive" ? "text-gray-300" : "text-gray-400"} />
                            <span className={`text-sm font-medium ${
                              document.status === "Inactive" ? "text-gray-500" : "text-gray-900"
                            }`}>
                              {getUserName(document.createdBy)}
                            </span>
                          </div>
                          {document.createdAt && (
                            <>
                              <span className={`text-xs ${
                                document.status === "Inactive" ? "text-gray-400" : "text-gray-500"
                              }`}>
                                {new Date(document.createdAt).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </span>
                              <span className={`text-xs ${
                                document.status === "Inactive" ? "text-gray-400" : "text-gray-400"
                              }`}>
                                {new Date(document.createdAt).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </>
                          )}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="p-4">
                        <div className="flex items-center justify-center gap-3">
                          <ToggleStatusCell document={document} onRefresh={onRefresh} />

                          <button
                            onClick={() => onView?.(document)}
                            title="View Document Details"
                            className={`transition-colors duration-200 ${
                              document.status === "Inactive"
                                ? "text-gray-300 hover:text-gray-400"
                                : "text-gray-400 hover:text-blue-400"
                            }`}
                          >
                            <Eye size={16} />
                          </button>

                          <button
                            onClick={() => onEdit?.(document)}
                            title="Edit Document"
                            className={`transition-colors duration-200 ${
                              document.status === "Inactive"
                                ? "text-gray-300 hover:text-gray-400"
                                : "text-gray-400 hover:text-emerald-400"
                            }`}
                          >
                            <Edit size={16} />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {!loading && documents.length === 0 && (
        <div className="p-16 text-center">
          <div className="w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-6 border border-gray-200"
            style={{ background: `linear-gradient(135deg, ${BRAND_COLORS.primary}20, ${BRAND_COLORS.primary}40)` }}
          >
            <FileText size={24} className="text-gray-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">No documents found</h3>
          <p className="text-gray-600 mb-8 max-w-md mx-auto leading-relaxed">
            {search || status
              ? "Try adjusting your search or filter criteria to find the documents you're looking for"
              : "Get started by adding your first document to the system"}
          </p>
          {!search && !status && (
            <button
              onClick={onAdd}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white border border-transparent rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 transform hover:-translate-y-0.5"
              style={{
                background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
                '--tw-ring-color': BRAND_COLORS.primary
              } as React.CSSProperties}
            >
              <Plus size={16} />
              Add First Document
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      {!loading && documents.length > 0 && (
        <div className="px-5 py-4 border-t border-gray-200 bg-gradient-to-r from-gray-25 to-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="text-sm text-gray-600 font-medium">
              Showing <span className="font-semibold" style={{ color: BRAND_COLORS.primary }}>{Math.min((page - 1) * ITEMS_PER_PAGE + 1, totalDocuments)}</span> to{" "}
              <span className="font-semibold" style={{ color: BRAND_COLORS.primary }}>{Math.min(page * ITEMS_PER_PAGE, totalDocuments)}</span> of{" "}
              <span className="font-semibold" style={{ color: BRAND_COLORS.primary }}>{totalDocuments}</span> documents
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(page - 1, 1))}
                disabled={page === 1}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
              >
                Previous
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`inline-flex items-center justify-center w-8 h-8 text-sm font-medium rounded-md transition-all duration-200 ${
                        page === pageNum
                          ? "text-white border border-transparent"
                          : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                      }`}
                      style={page === pageNum ? {
                        background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`
                      } : {}}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setPage(Math.min(page + 1, totalPages))}
                disabled={page === totalPages}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}
